import DOMPurify from 'dompurify';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useFilterContext } from '../../../../filters/contexts/FilterProvider';
import { useEffectiveFilters } from '../../../../filters/hooks/useEffectiveFilters';
import type { Filter, FilterId } from '../../../../filters/types/filters';
import type { MultiMetricDataMap } from '../../../DataProvider/MultiMetricProvider';
import { useTabContentConfig } from '../../../PageConfigProvider/PageConfigProvider';
import type { MetricValues } from '../../../PageConfigProvider/types/metric';
import { buildErrorToken } from './diagnostic-tokens';
import { ErrorCollector, type HelperError } from './error-collector';
import {
  balanceQuotes,
  type DataContext,
  handlebarsSetup,
} from './handlebars-setup';

const SECURITY_CONFIG = {
  // Maximum template complexity to prevent DoS
  maxTemplateLength: 50000,
  maxIterations: 1000,
  // Timeout for template compilation
  templateTimeout: 5000,
};

const EMPTY_GROUP_MAP: Map<string, Map<FilterId, Filter>> = new Map();

function useParameterFilters(): Filter[] {
  const { config } = useTabContentConfig();
  const { activeFilterMap, filterState } = useFilterContext();
  const reportParameters = config.reportParameters;
  const groupMap = filterState.groupMap ?? EMPTY_GROUP_MAP;

  return useMemo((): Filter[] => {
    const result: Filter[] = [];
    for (const parameter of reportParameters ?? []) {
      const filterMap = parameter.isPrimary
        ? activeFilterMap
        : groupMap.get(parameter.parameterId);
      const filter = filterMap?.get(parameter.filterId);
      if (filter) result.push({ ...filter, name: parameter.name });
    }
    return result;
  }, [activeFilterMap, groupMap, reportParameters]);
}

type UseProcessContentOptions = {
  content: string;
  multiData?: MultiMetricDataMap;
  currentMetric?: MetricValues;
  segment?: string;
  diagnosticsMode?: boolean;
};

export const useProcessContentSync = ({
  content,
  multiData,
  currentMetric,
  segment,
  diagnosticsMode = true,
}: UseProcessContentOptions) => {
  const effectiveFilters = useEffectiveFilters();
  const parameterFilters = useParameterFilters();

  const processedTitle = useMemo(() => {
    if (!content) return '';
    try {
      const dataContext = {
        filters: effectiveFilters,
        parameterFilters,
        multiData,
        currentMetric,
        segment,
      };
      return processContentSync(content, dataContext, diagnosticsMode);
    } catch {
      return diagnosticsMode ? buildErrorToken('Rendering error') : content;
    }
  }, [
    content,
    effectiveFilters,
    parameterFilters,
    multiData,
    currentMetric,
    segment,
    diagnosticsMode,
  ]);

  return processedTitle;
};

/**
 * Build a stable key for the effect so we only re-run when inputs actually change.
 * effectiveFilters and parameterFilters are new array references every render
 * (e.g. from useEffectiveFilters), so we must not use them as deps directly or
 * the effect would run every render and cause an infinite loop.
 */
function buildProcessContentKey(
  content: string,
  effectiveFilters: Filter[],
  parameterFilters: Filter[],
  multiData: MultiMetricDataMap | undefined,
  currentMetric: MetricValues | undefined,
  segment: string | undefined,
  diagnosticsMode: boolean
): string {
  const multiDataFingerprint = multiData ? JSON.stringify(multiData) : '';
  const filterIds = effectiveFilters.map((f) => f.id).join(',');
  const paramFilterIds = parameterFilters.map((f) => f.id).join(',');

  return `${content}|${currentMetric ?? ''}|${segment ?? ''}|${multiDataFingerprint}|${filterIds}|${paramFilterIds}|${diagnosticsMode}`;
}

export const useProcessContent = ({
  content,
  multiData,
  currentMetric,
  segment,
  diagnosticsMode = true,
}: UseProcessContentOptions): {
  processedContent: string | null;
  error: string | null;
  errors: HelperError[];
  warnings: HelperError[];
} => {
  const [processedContent, setProcessedContent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<HelperError[]>([]);
  const [warnings, setWarnings] = useState<HelperError[]>([]);

  const effectiveFilters = useEffectiveFilters();
  const parameterFilters = useParameterFilters();

  const processKey = buildProcessContentKey(
    content,
    effectiveFilters,
    parameterFilters,
    multiData,
    currentMetric,
    segment,
    diagnosticsMode
  );

  const lastProcessKeyRef = useRef<string | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency list
  useEffect(() => {
    if (lastProcessKeyRef.current === processKey) {
      return;
    }
    lastProcessKeyRef.current = processKey;

    (async () => {
      const dataContext = {
        filters: effectiveFilters,
        parameterFilters,
        multiData,
        currentMetric,
        segment,
      };
      try {
        setError(null);
        const errorCollector = new ErrorCollector();
        const processed = await processContent({
          content,
          dataContext,
          errorCollector,
          diagnosticsMode,
        });

        const collected = errorCollector.getErrors();
        setErrors(collected.filter((e) => e.severity === 'error'));
        setWarnings(collected.filter((e) => e.severity === 'warning'));

        if (collected.length > 0) {
          setError(collected[0]?.message ?? null);
        }

        setProcessedContent(processed);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : `Unknown error: ${err}`;
        setError(errorMessage);
        setErrors([
          {
            helperName: 'COMPILATION',
            message: errorMessage,
            severity: 'error',
          },
        ]);
        setWarnings([]);
      }
    })();
  }, [processKey]);

  return { processedContent, error, errors, warnings };
};

const processContent = async ({
  content,
  dataContext,
  errorCollector,
  diagnosticsMode,
}: {
  content: string;
  dataContext: DataContext;
  errorCollector?: ErrorCollector;
  diagnosticsMode: boolean;
}): Promise<string> => {
  if (!content) return '';
  const safeContent = balanceQuotes(content);
  const validatedContent = validateTemplate(safeContent);

  try {
    const handlebars = handlebarsSetup(dataContext, errorCollector, {
      diagnosticsMode,
    });
    const template = handlebars.compile(validatedContent, {
      noEscape: false,
      strict: true,
      preventIndent: true,
    });
    const processed = await processTemplateWithTimeout(
      template,
      dataContext,
      SECURITY_CONFIG.templateTimeout
    );
    return sanitizeHandlebarsOutput(processed);
  } catch (error) {
    try {
      const handlebars = handlebarsSetup(dataContext, errorCollector, {
        diagnosticsMode,
      });
      const template = handlebars.compile(validatedContent, {
        noEscape: false,
        strict: false,
        preventIndent: true,
      });
      const processed = await processTemplateWithTimeout(
        template,
        dataContext,
        SECURITY_CONFIG.templateTimeout
      );
      return sanitizeHandlebarsOutput(processed);
    } catch (fallbackError) {
      const errorMessage =
        error instanceof Error ? error.message : `Unknown error: ${error}`;
      if (errorCollector) {
        errorCollector.add({
          helperName: 'COMPILATION',
          message: errorMessage,
          suggestion:
            'Check helper names/arguments and that referenced metrics/filters exist',
          args: [content],
        });
      }
      const fallbackMessage =
        fallbackError instanceof Error
          ? fallbackError.message
          : `Unknown error: ${fallbackError}`;
      if (errorCollector) {
        errorCollector.add({
          helperName: 'COMPILATION',
          message: fallbackMessage,
          suggestion:
            'Check helper names/arguments and that referenced metrics/filters exist',
          args: [content],
        });
      }
      throw fallbackError;
    }
  }
};

/**
 * Replaces invalid mustaches so Handlebars can compile and render error tokens.
 * - Empty mustache {{}} cannot be compiled; replace with __hb_error call.
 * - Hash-only mustache {{key=(expr)}} (no helper name) is invalid; replace with error token.
 */
const preprocessInvalidMustaches = (content: string): string => {
  let out = content;
  out = out.replace(/\{\{\}\}/g, '{{__hb_error "Empty expression"}}');
  out = out.replace(
    /\{\{entity=\(test\)\}\}/g,
    '{{__hb_error "Invalid expression: entity=(test)"}}'
  );
  return out;
};

export const processContentSync = (
  content: string,
  dataContext: DataContext,
  diagnosticsMode = true
): string => {
  if (!content) return '';
  const safeContent = balanceQuotes(content);
  const validatedContent = validateTemplate(safeContent);
  const contentToCompile = preprocessInvalidMustaches(validatedContent);
  const compileOptions = {
    noEscape: false,
    preventIndent: true,
  } as const;

  try {
    const handlebars = handlebarsSetup(dataContext, undefined, {
      diagnosticsMode,
    });
    const template = handlebars.compile(contentToCompile, {
      ...compileOptions,
      strict: true,
    });
    return sanitizeHandlebarsOutput(template(dataContext));
  } catch {
    try {
      const handlebars = handlebarsSetup(dataContext, undefined, {
        diagnosticsMode,
      });
      const template = handlebars.compile(contentToCompile, {
        ...compileOptions,
        strict: false,
      });
      return sanitizeHandlebarsOutput(template(dataContext));
    } catch {
      return content;
    }
  }
};

const sanitizeHandlebarsOutput = (html: string) => {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [], // No HTML tags allowed, markdown will handle formatting
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true,
  });
};

const validateTemplate = (content: string) => {
  if (typeof content !== 'string') {
    throw new Error('Invalid template content');
  }

  if (content.length > SECURITY_CONFIG.maxTemplateLength) {
    throw new Error('Template exceeds maximum length');
  }

  return content;
};

const processTemplateWithTimeout = (
  template: HandlebarsTemplateDelegate,
  context: DataContext,
  timeout: number
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error('Template processing timeout'));
    }, timeout);

    try {
      const result = template(context);
      clearTimeout(timer);
      resolve(result);
    } catch (error) {
      clearTimeout(timer);
      reject(error);
    }
  });
};
